import { LayoutHeader } from "@/Header";
import { ValidateSelectBoxImg } from "@/SelectBoxImg";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { format, subMonths, startOfMonth } from "date-fns";
import {
  useBusinessMypageCustomerDetail,
  useCustomerTicketUsage,
  usePetMemo,
  useSavePetMemo,
  type CustomerTicketUsageBundle,
} from "~/services/business/myPage";
import { formatPhoneNumber } from "%/dataTransform";
import defaultPet from "$/defaultLogo.svg";

interface CustomerManagementDetailSearch {
  userSeq: number;
  petSeq?: string;
}

export const Route = createFileRoute(
  "/business/layout/user-customer-management-detail",
)({
  component: CustomerManagementDetail,
  validateSearch: (
    search: Record<string, unknown>,
  ): CustomerManagementDetailSearch => {
    // TanStack Router는 ?petSeq=102 를 JSON 파싱하여 number 102로 넘겨줄 수 있어
    // string/number 모두 받아 문자열로 정규화한다.
    const rawPetSeq = search.petSeq;
    let petSeq: string | undefined;
    if (typeof rawPetSeq === "number" && Number.isFinite(rawPetSeq)) {
      petSeq = String(rawPetSeq);
    } else if (typeof rawPetSeq === "string" && rawPetSeq.length > 0) {
      petSeq = rawPetSeq;
    }
    return {
      userSeq: Number(search.userSeq) || 0,
      petSeq,
    };
  },
});

function CustomerManagementDetail() {
  const { history } = useRouter();
  const { userSeq, petSeq } = Route.useSearch();

  const [selectedPetSeq, setSelectedPetSeq] = useState<string>(petSeq ?? "");

  const { data: customerDetailData, isLoading } =
    useBusinessMypageCustomerDetail({ userSeq });

  // 진입 시 petSeq 미지정이면 첫 번째 반려동물을 기본 선택
  useEffect(() => {
    if (selectedPetSeq) return;
    const firstPet = customerDetailData?.data?.pets?.[0];
    if (firstPet) {
      setSelectedPetSeq(firstPet.petSeq.toString());
    }
  }, [customerDetailData, selectedPetSeq]);

  // 원본 모달과 동일하게 런타임 응답을 그대로 사용 (petBreed 등 타입 정의에 없는 필드 포함)
  const selectedPet = customerDetailData?.data?.pets.find(
    (pet: any) => pet.petSeq.toString() === selectedPetSeq,
  ) as any;

  const selectedPetSeqNumber = Number(selectedPetSeq) || 0;

  // 이용권 사용내역 기간 필터 (모든 펫 공통)
  const [rangePreset, setRangePreset] = useState<"all" | "1m" | "3m" | "6m">(
    "all",
  );
  const { from, to } = useMemo(() => computeRange(rangePreset), [rangePreset]);

  // ── 매장 전용 펫 메모 / 블랙리스트 (보호자 미노출) ──
  const { data: petMemo } = usePetMemo(
    selectedPetSeqNumber > 0 ? selectedPetSeqNumber : undefined,
  );
  const savePetMemoMutation = useSavePetMemo();
  const [memoContent, setMemoContent] = useState("");
  const [blacklistYn, setBlacklistYn] = useState(false);
  // 조회 결과가 오면 편집 상태에 반영 (펫 변경 시마다 갱신)
  useEffect(() => {
    setMemoContent(petMemo?.memoContent ?? "");
    setBlacklistYn(petMemo?.blacklistYn === "Y");
  }, [petMemo, selectedPetSeqNumber]);

  const handleSaveMemo = () => {
    if (selectedPetSeqNumber <= 0) return;
    savePetMemoMutation.mutate(
      {
        petSeq: selectedPetSeqNumber,
        memoContent,
        blacklistYn: blacklistYn ? "Y" : "N",
        blacklistReason: null,
      },
      {
        onSuccess: () => alert("저장되었습니다."),
        onError: () => alert("저장에 실패했습니다. 다시 시도해주세요."),
      },
    );
  };

  const handleBackClick = () => {
    history.back();
  };

  return (
    <>
      <LayoutHeader
        title="고객 정보 상세"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={handleBackClick}
      />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5] px-4 pt-4">
        {/* 잘못된 진입 방어 */}
        {!userSeq ? (
          <div className="flex flex-col justify-center items-center py-20 text-[#666666]">
            <p className="mb-2">고객 정보가 없습니다.</p>
            <p className="text-sm">목록에서 고객을 선택해 주세요.</p>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center items-center py-20 text-[#666666]">
            <p>불러오는 중...</p>
          </div>
        ) : !customerDetailData?.data ? (
          <div className="flex justify-center items-center py-20 text-[#666666]">
            <p>고객 정보를 찾을 수 없습니다.</p>
          </div>
        ) : (
          <>
            {/* 사용자 정보 섹션 */}
            <div className="p-4 mb-6 bg-white rounded-2xl">
              <div className="grid grid-cols-10 gap-2 items-center mb-2">
                <div className="col-span-3 text-[14px] text-left font-semibold">
                  고객명
                </div>
                <div className="col-span-7 text-[13px] text-left">
                  {customerDetailData.data.userName}
                </div>
              </div>
              <div className="grid grid-cols-10 gap-2 items-center mb-2">
                <div className="col-span-3 text-[14px] text-left font-semibold">
                  이메일
                </div>
                <div className="col-span-7 text-[13px] text-left">
                  {customerDetailData.data.email}
                </div>
              </div>
              <div className="grid grid-cols-10 gap-2 items-center mb-2">
                <div className="col-span-3 text-[14px] text-left font-semibold">
                  휴대폰 번호
                </div>
                <div className="col-span-7 text-[13px] text-left">
                  {formatPhoneNumber(customerDetailData.data.phoneNumber)}
                </div>
              </div>
              <div className="grid grid-cols-10 gap-2 items-center">
                <div className="col-span-3 text-[14px] text-left font-semibold">
                  닉네임
                </div>
                <div className="col-span-7 text-[13px] text-left">
                  {customerDetailData.data.nickName}
                </div>
              </div>
            </div>

            {/* 반려동물 정보 확인 섹션 */}
            <div className="mb-4">
              <h4 className="mb-3 text-[14px] font-semibold">
                반려동물 정보 확인
              </h4>
              <ValidateSelectBoxImg
                max={1}
                defaultValues={selectedPetSeq ? [selectedPetSeq] : []}
                onChange={(values) => setSelectedPetSeq(values[0] || "")}
                options={customerDetailData.data.pets.map((pet: any) => ({
                  value: pet.petSeq.toString(),
                  label: pet.petName,
                  // 사진 미등록 시 외부 스톡 이미지 대신 서비스 기본 이미지를 쓴다.
                  // (남의 강아지 사진이 해당 고객의 반려견처럼 보이던 문제)
                  imgSrc: pet.fileUrl || defaultPet,
                }))}
              />
            </div>

            {/* 선택된 반려동물 상세 정보 */}
            {selectedPet && (
              <div className="p-4 bg-white rounded-2xl">
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    이름
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.petName}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    품종
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.petBreed}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    나이
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.petBirthday}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    중성화
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.neuterYn === "Y" ? "완료" : "미완료"}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    몸무게
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.petWeight}kg
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    접종정보
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.inoculation}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    성향
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.personality}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center mb-2">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    알레르기
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.allergy}
                  </div>
                </div>
                <div className="grid grid-cols-10 gap-2 items-center">
                  <div className="col-span-3 text-[14px] text-left font-semibold">
                    기타 정보
                  </div>
                  <div className="col-span-7 text-[14px] text-left">
                    {selectedPet.memo}
                  </div>
                </div>
              </div>
            )}

            {/* 매장 전용 메모 / 블랙리스트 — 보호자에게 노출되지 않는 매장 내부 관리용 */}
            {selectedPetSeqNumber > 0 && (
              <div className="mt-4 p-4 bg-white rounded-2xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[14px] font-semibold">매장 메모</h4>
                    <span className="text-[11px] text-[#999]">
                      (보호자 미노출)
                    </span>
                    {blacklistYn && (
                      <span className="rounded-full bg-[#FDECEC] px-2 py-0.5 text-[11px] font-bold text-[#E5484D]">
                        블랙리스트
                      </span>
                    )}
                  </div>
                  <label className="flex items-center gap-1 text-[12px] text-[#666] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={blacklistYn}
                      onChange={(e) => setBlacklistYn(e.target.checked)}
                    />
                    블랙리스트
                  </label>
                </div>
                <textarea
                  value={memoContent}
                  onChange={(e) => setMemoContent(e.target.value)}
                  placeholder="고지되지 않은 특이사항, 주의사항 등 매장 내부 메모를 남겨주세요."
                  rows={3}
                  className="w-full resize-none rounded-lg border border-[#E9E9E9] px-3 py-2 text-[13px] focus:border-[#FF9934] focus:outline-none"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveMemo}
                    disabled={savePetMemoMutation.isPending}
                    className="rounded-lg bg-[#FF9934] px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50"
                  >
                    {savePetMemoMutation.isPending ? "저장 중…" : "저장"}
                  </button>
                </div>
              </div>
            )}

            {/* 이용권 사용내역 — 위에서 선택한 반려동물 것만, 구매 묶음 + 회차별 이용일자 */}
            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-[14px] font-semibold">
                  이용권 사용내역
                  {selectedPet?.petName ? ` · ${selectedPet.petName}` : ""}
                </h4>
                <div className="flex gap-1.5">
                  {RANGE_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setRangePreset(p.key)}
                      className={`rounded-full px-3 py-1 text-[12px] transition-colors ${
                        rangePreset === p.key
                          ? "bg-[#FF9934] text-white"
                          : "border border-[#e9e9e9] bg-white text-[#666666]"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              {(customerDetailData.data.pets ?? []).length === 0 ? (
                <div className="p-4 text-center text-[#666666] bg-white rounded-2xl">
                  등록된 반려동물이 없습니다.
                </div>
              ) : !selectedPet ? (
                <div className="p-4 text-center text-[#666666] bg-white rounded-2xl">
                  반려동물을 선택해 주세요.
                </div>
              ) : (
                // 선택한 반려동물의 내역만 노출한다.
                // (예전에는 전체 펫을 나열해 선택과 무관하게 다른 반려견 이용권까지 보였다)
                <PetTicketUsage
                  key={selectedPetSeqNumber}
                  petSeq={selectedPetSeqNumber}
                  petName={selectedPet.petName}
                  from={from}
                  to={to}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ─────────── 이용권 사용내역 (모듈 스코프) ─────────── */

const RANGE_PRESETS = [
  { key: "all", label: "전체" },
  { key: "1m", label: "이번 달" },
  { key: "3m", label: "3개월" },
  { key: "6m", label: "6개월" },
] as const;

/** 기간 프리셋 → from/to(YYYY-MM-DD). 전체는 미지정. */
function computeRange(preset: "all" | "1m" | "3m" | "6m"): {
  from?: string;
  to?: string;
} {
  const today = new Date();
  const fmt = (d: Date) => format(d, "yyyy-MM-dd");
  switch (preset) {
    case "1m":
      return { from: fmt(startOfMonth(today)), to: fmt(today) };
    case "3m":
      return { from: fmt(subMonths(today, 3)), to: fmt(today) };
    case "6m":
      return { from: fmt(subMonths(today, 6)), to: fmt(today) };
    default:
      return {};
  }
}

const getStatusLabel = (code?: string | null): string => {
  switch (code) {
    case "ISSUE":
      return "발급";
    case "REVOKE":
      return "회수";
    case "USE":
      return "사용중";
    case "EXPIRED":
      return "만료";
    case "CANCEL":
      return "취소";
    default:
      return code || "-";
  }
};

/** 예약 유형 코드(RESERV_TYPE_*) → 간단 라벨 (미상은 "이용") */
const serviceTypeLabel = (t?: string | null): string => {
  if (!t) return "이용";
  const s = t.toUpperCase();
  if (s.includes("GROOM") || s.includes("BEAUTY")) return "미용";
  if (s.includes("HOTEL")) return "호텔";
  if (s.includes("DAYCARE")) return "돌봄";
  if (s.includes("CLASS") || s.includes("KINDER")) return "유치원";
  if (s.includes("CARE")) return "돌봄";
  if (s.includes("BATH")) return "목욕";
  return "이용";
};

function PetTicketUsage({
  petSeq,
  petName,
  from,
  to,
}: {
  petSeq: number;
  petName: string;
  from?: string;
  to?: string;
}) {
  const { data, isLoading } = useCustomerTicketUsage(petSeq, from, to);
  const bundles = data?.bundles ?? [];
  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FFF1DD] text-[11px] font-semibold text-[#D97A15]">
          {(petName || "?").slice(0, 1)}
        </div>
        <span className="text-[13px] font-semibold text-[#111]">{petName}</span>
        {data && (
          <span className="ml-auto text-[12px] text-[#666666]">
            {data.useCount}회
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="rounded-2xl bg-white p-3 text-center text-[12px] text-[#999]">
          불러오는 중…
        </div>
      ) : bundles.length === 0 ? (
        <div className="rounded-2xl bg-white p-3 text-center text-[12px] text-[#999]">
          이용권 내역이 없습니다.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {bundles.map((b) => (
            <BundleUsageCard key={b.purchaseTicketSeq} bundle={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function BundleUsageCard({ bundle }: { bundle: CustomerTicketUsageBundle }) {
  const [expanded, setExpanded] = useState(false);
  const uses = bundle.uses ?? [];
  const shown = expanded ? uses : uses.slice(0, 3);
  return (
    <div className="rounded-2xl border border-[#e9e9e9] bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="break-words pr-2 text-[13px] font-semibold text-[#111]">
          {bundle.ticketName}
        </div>
        <div className="whitespace-nowrap text-[12px] text-[#D8730F]">
          잔여 {bundle.remainQty ?? 0}/{bundle.qty ?? 0}회
        </div>
      </div>
      <div className="mt-1 text-[11px] text-[#999]">
        {bundle.dtRegist ? `구매 ${bundle.dtRegist.slice(0, 10)}` : ""}
        {bundle.dtExpired
          ? ` · 만료 ${bundle.dtExpired.slice(0, 10)}`
          : ""} · {getStatusLabel(bundle.status)}
      </div>
      <div className="mt-2 border-t border-dashed border-[#eee] pt-2">
        {uses.length === 0 ? (
          <div className="py-1 text-center text-[11px] text-[#bbb]">
            이 기간 사용 내역 없음
          </div>
        ) : (
          <>
            {shown.map((u) => {
              // 이용권이 차감된 시각(dtUse)이 아니라 실제 예약을 이용한 날(dtUsed)을 보여준다.
              const usedDate = u.dtUsed ?? u.dtUse;
              const canceled = u.status === "RESERV_STATUS_CANCELED";
              return (
                <div
                  key={u.ticketUseSeq}
                  className="flex items-center gap-2 border-b border-[#f3f3f3] py-1 last:border-0"
                >
                  <span
                    className={`text-[13px] font-medium ${
                      canceled ? "text-[#999] line-through" : "text-[#111]"
                    }`}
                  >
                    {usedDate ? usedDate.slice(0, 10) : "-"}
                  </span>
                  {canceled && (
                    <span className="rounded-full bg-[#F3F3F3] px-1.5 py-0.5 text-[10px] text-[#888]">
                      취소
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-[#666666]">
                    {serviceTypeLabel(u.reservType)} · {u.qty ?? 1}회 차감
                  </span>
                </div>
              );
            })}
            {uses.length > 3 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-1 w-full text-center text-[11px] text-[#FF9934]"
              >
                {expanded ? "접기" : `${uses.length - 3}건 더보기`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
